import { create } from 'zustand'
import { persist, devtools } from 'zustand/middleware'
import type { User, LoginRequest, RegisterRequest, OnboardingData } from '@/types'
import type { AppleCredential } from '@/platform'
import { clearSignupTouches, signupTouches } from '@/lib/signupTouch'
import { accessCodeMessage } from '@/lib/signupAccess'
import { api, refreshAccessToken, tokenManager, isApiError } from '@/api/client'
import {
  PERSISTED_STORE_KEYS,
  clearPersistedStores,
  registerPersistedStore,
} from '@/stores/persistedKeys'
import type { LoginResponse } from '@/types'

// Auth store state interface
interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  onboarding: OnboardingData | null
  // Set when login()/googleLogin()/appleLogin() return an MFA challenge instead of tokens
  // (admin account with MFA enabled, #2572). Not persisted — a page refresh
  // mid-challenge should require starting the login over.
  pendingMfaToken: string | null
}

// Result of a login — the caller checks mfaRequired to decide
// whether to navigate to the MFA code screen instead of the app.
interface LoginOutcome {
  mfaRequired: boolean
}

// Auth store actions interface
interface AuthActions {
  login: (credentials: LoginRequest) => Promise<LoginOutcome>
  googleLogin: (idToken: string, accessCode?: string) => Promise<LoginOutcome>
  appleLogin: (credential: AppleCredential, accessCode?: string) => Promise<LoginOutcome>
  verifyMfaCode: (code: string) => Promise<void>
  register: (data: RegisterRequest) => Promise<void>
  logout: () => void
  refreshToken: () => Promise<void>
  fetchCurrentUser: () => Promise<void>
  updateOnboarding: (data: Partial<OnboardingData>) => void
  clearError: () => void
  setUser: (user: User | null) => void
}

// Combined store type
type AuthStore = AuthState & AuthActions

// Initial state
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  onboarding: null,
  pendingMfaToken: null,
}

/**
 * A fresh sign-in starts a fresh local session.
 *
 * Closing the browser and signing in as somebody else never calls `logout()`,
 * so without this the previous account's persisted stores are still hydrated
 * when the new user lands (#1923). `auth-storage` is excluded because this runs
 * mid-login, and wiping it would discard the state the login flow is writing.
 */
const clearPreviousSession = () => clearPersistedStores({ except: [PERSISTED_STORE_KEYS.auth] })

// Create the auth store with persist and devtools middleware
export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      (set, get) => {
        // Google and Apple sign-in: the provider's credential goes to `path`; the
        // answer is tokens (a new account starts onboarding) or an MFA challenge.
        const providerLogin = async (
          path: string,
          credential: Record<string, string | null>,
          accessCode: string | undefined,
          failureMessage: string
        ): Promise<LoginOutcome> => {
          set({ isLoading: true, error: null })
          clearPreviousSession()
          try {
            const res = await api.post<LoginResponse>(path, {
              ...credential,
              touch: signupTouches(),
              access_code: accessCode || undefined,
            })
            const auth = (res?.data ?? res) as unknown as {
              mfa_required?: boolean
              mfa_token?: string
              access_token?: string
              refresh_token?: string
              user_id?: string
            }
            if (auth.mfa_required && auth.mfa_token) {
              set({ pendingMfaToken: auth.mfa_token, isLoading: false })
              return { mfaRequired: true }
            }
            tokenManager.setTokens(auth.access_token!, auth.refresh_token!)
            clearSignupTouches()
            const userRes = await api.get<User>('/auth/me')
            const user = (userRes?.data ?? userRes) as unknown as User
            const needsOnboarding = !user.default_business_id
            set({
              user,
              isAuthenticated: true,
              isLoading: false,
              ...(needsOnboarding && { onboarding: { completed: false, currentStep: 0 } }),
            })
            return { mfaRequired: false }
          } catch (error) {
            const message =
              accessCodeMessage(error) ?? (isApiError(error) ? error.message : failureMessage)
            set({ error: message, isLoading: false })
            throw error
          }
        }

        return {
          ...initialState,

          login: async (credentials: LoginRequest) => {
            set({ isLoading: true, error: null })
            clearPreviousSession()
            try {
              // Backend returns flat snake_case tokens, OR {mfa_required: true,
              // mfa_token} for an MFA-enrolled admin (#2572).
              const res = await api.post<LoginResponse>('/auth/login', credentials)
              const auth = (res?.data ?? res) as unknown as {
                mfa_required?: boolean
                mfa_token?: string
                access_token?: string
                refresh_token?: string
                user_id?: string
              }
              if (auth.mfa_required && auth.mfa_token) {
                set({ pendingMfaToken: auth.mfa_token, isLoading: false })
                return { mfaRequired: true }
              }
              tokenManager.setTokens(auth.access_token!, auth.refresh_token!)
              // Fetch full user profile after login
              const userRes = await api.get<User>('/auth/me')
              const user = (userRes?.data ?? userRes) as unknown as User
              set({ user, isAuthenticated: true, isLoading: false })
              return { mfaRequired: false }
            } catch (error) {
              const message = isApiError(error) ? error.message : 'Login failed'
              set({ error: message, isLoading: false })
              throw error
            }
          },

          googleLogin: (idToken: string, accessCode?: string) =>
            providerLogin('/auth/google', { id_token: idToken }, accessCode, 'Google login failed'),

          appleLogin: (credential: AppleCredential, accessCode?: string) =>
            providerLogin(
              '/auth/apple',
              {
                identity_token: credential.identityToken,
                authorization_code: credential.authorizationCode,
                name: credential.name,
              },
              accessCode,
              'Sign in with Apple failed'
            ),

          verifyMfaCode: async (code: string) => {
            const mfaToken = get().pendingMfaToken
            if (!mfaToken) {
              set({ error: 'Login session expired — please sign in again.' })
              throw new Error('No pending MFA challenge')
            }
            set({ isLoading: true, error: null })
            try {
              const res = await api.post<LoginResponse>('/auth/mfa/verify', {
                mfa_token: mfaToken,
                code,
              })
              const auth = (res?.data ?? res) as unknown as {
                access_token: string
                refresh_token: string
              }
              tokenManager.setTokens(auth.access_token, auth.refresh_token)
              const userRes = await api.get<User>('/auth/me')
              const user = (userRes?.data ?? userRes) as unknown as User
              set({ user, isAuthenticated: true, isLoading: false, pendingMfaToken: null })
            } catch (error) {
              const message = isApiError(error) ? error.message : 'Invalid or expired code'
              set({ error: message, isLoading: false })
              throw error
            }
          },

          register: async (data: RegisterRequest) => {
            set({ isLoading: true, error: null })
            clearPreviousSession()
            try {
              // Backend returns flat snake_case: {access_token, refresh_token, user_id}
              const res = await api.post<LoginResponse>('/auth/register', {
                ...data,
                touch: signupTouches(),
              })
              const auth = (res?.data ?? res) as unknown as {
                access_token: string
                refresh_token: string
                user_id: string
              }
              tokenManager.setTokens(auth.access_token, auth.refresh_token)
              clearSignupTouches()
              // Fetch full user profile after registration
              const userRes = await api.get<User>('/auth/me')
              const user = (userRes?.data ?? userRes) as unknown as User
              set({
                user,
                isAuthenticated: true,
                isLoading: false,
                onboarding: { completed: false, currentStep: 0 },
              })
            } catch (error) {
              // The backend returns field-specific reasons in `details` (e.g.
              // "Password must contain..."). Surface those instead of the generic
              // top-level "Request validation failed" so the user knows what to fix.
              let message =
                accessCodeMessage(error) ??
                (isApiError(error) ? error.message : 'Registration failed')
              const details = (error as { details?: unknown }).details
              if (Array.isArray(details)) {
                const reasons = details
                  .map((d) => (d as { msg?: string }).msg?.replace(/^Value error,\s*/, ''))
                  .filter((m): m is string => !!m)
                if (reasons.length) message = reasons.join(' ')
              }
              set({ error: message, isLoading: false })
              throw error
            }
          },

          logout: () => {
            tokenManager.clearTokens()
            // Erase EVERY persisted store, not just the tokens. Conversation
            // titles and full message bodies live in `ai-chat-storage`, which used
            // to survive a logout and render for the next account to sign in
            // (#1923). This also performs this store's own `set(initialState)` —
            // it is registered below — so resetting again here would only make
            // zustand re-write an empty `auth-storage` entry after we removed it.
            clearPersistedStores()
          },

          refreshToken: async () => {
            // Shared single-flight helper — dedupes against any concurrent
            // refresh (e.g. media chunk uploads recovering from a 401).
            const accessToken = await refreshAccessToken()
            if (!accessToken) {
              get().logout()
            }
          },

          fetchCurrentUser: async () => {
            if (!tokenManager.hasValidToken()) {
              return
            }

            set({ isLoading: true })
            try {
              const response = await api.get<User>('/auth/me')
              const user = (response?.data ?? response) as unknown as User
              set({ user, isAuthenticated: true, isLoading: false, error: null })
            } catch (error) {
              // Don't flip isAuthenticated here — a thrown error can be a
              // network blip, a timeout, or a 5xx just as easily as a genuine
              // auth failure. Callers (AuthProvider, AuthAcceptPage) classify
              // the error and decide whether it warrants logging out; logout()
              // is the only place that clears isAuthenticated.
              const message = isApiError(error) ? error.message : 'Failed to fetch user'
              set({ error: message, isLoading: false })
              throw error
            }
          },

          updateOnboarding: (data: Partial<OnboardingData>) => {
            const currentOnboarding = get().onboarding
            set({
              onboarding: {
                ...currentOnboarding,
                ...data,
                completed: data.completed ?? currentOnboarding?.completed ?? false,
                currentStep: data.currentStep ?? currentOnboarding?.currentStep ?? 0,
              },
            })
          },

          clearError: () => {
            set({ error: null })
          },

          setUser: (user: User | null) => {
            set({ user, isAuthenticated: !!user })
          },
        }
      },
      {
        name: PERSISTED_STORE_KEYS.auth,
        partialize: (state) => ({
          user: state.user,
          isAuthenticated: state.isAuthenticated,
          onboarding: state.onboarding,
        }),
      }
    ),
    { name: 'AuthStore', enabled: import.meta.env.DEV }
  )
)

registerPersistedStore(PERSISTED_STORE_KEYS.auth, () => useAuthStore.setState(initialState))

// Selectors for common access patterns
export const selectUser = (state: AuthStore) => state.user
export const selectIsAuthenticated = (state: AuthStore) => state.isAuthenticated
export const selectIsLoading = (state: AuthStore) => state.isLoading
export const selectAuthError = (state: AuthStore) => state.error
export const selectOnboarding = (state: AuthStore) => state.onboarding
export const selectPendingMfaToken = (state: AuthStore) => state.pendingMfaToken
export const selectIsOnboardingComplete = (state: AuthStore) => state.onboarding?.completed ?? false
